import { useState } from "react";
import { Zap, CreditCard, Loader2, Check } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const PURCHASE_OPTIONS = [
  { label: "$5", amount: 500 },
  { label: "$10", amount: 1000 },
  { label: "$25", amount: 2500 },
];

export default function BillingTab() {
  const { user, buyCredits } = useAuth();
  const [loadingAmount, setLoadingAmount] = useState(null);

  const handleBuy = async (amountCents) => {
    setLoadingAmount(amountCents);
    try {
      await buyCredits(amountCents);
    } catch {
      // buyCredits redirects on success; errors are rare (network/Stripe)
    } finally {
      setLoadingAmount(null);
    }
  };

  if (!user) return null;

  const PurchaseButtons = () => (
    <div className="flex gap-2 mt-3">
      {PURCHASE_OPTIONS.map(({ label, amount }) => (
        <Button
          key={amount}
          variant="outline"
          onClick={() => handleBuy(amount)}
          disabled={loadingAmount !== null}
          className="flex-1 rounded-sm font-mono text-xs h-9"
          style={{
            borderColor: "var(--sdb-border)",
            color: loadingAmount === amount ? "var(--sdb-accent)" : "var(--sdb-text)",
          }}
        >
          {loadingAmount === amount ? (
            <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
          ) : label}
        </Button>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-6 p-1" style={{ color: "var(--sdb-text)" }}>

      {/* FREE TIER */}
      {!user.is_paid && (
        <>
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-[2px] uppercase" style={{ color: "var(--sdb-text-subtle)" }}>
              Current Plan
            </span>
            <Badge
              variant="outline"
              className="rounded-sm font-mono text-[10px]"
              style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text-muted)" }}
            >
              FREE TIER
            </Badge>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-end">
              <span className="text-2xl font-medium" style={{ color: "var(--sdb-accent)" }}>
                {3 - (user.free_generations_used || 0)}
                <span className="text-base font-normal ml-2" style={{ color: "var(--sdb-text-muted)" }}>
                  generations remaining
                </span>
              </span>
              <span className="font-mono text-xs" style={{ color: "var(--sdb-text-subtle)" }}>
                {user.free_generations_used || 0} / 3 used
              </span>
            </div>
            {/* Progress bar */}
            <div
              className="h-1.5 w-full rounded-sm overflow-hidden"
              style={{ background: "var(--sdb-surface)", border: "1px solid var(--sdb-border)" }}
            >
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${Math.min(((user.free_generations_used || 0) / 3) * 100, 100)}%`,
                  background: "var(--sdb-accent)",
                }}
              />
            </div>
          </div>

          {/* Upgrade callout */}
          <div
            className="p-5 rounded-sm"
            style={{
              border: "1px solid rgba(0,229,160,0.2)",
              background: "rgba(0,229,160,0.04)",
            }}
          >
            <h3 className="text-sm font-medium flex items-center gap-2 mb-3" style={{ color: "var(--sdb-text)" }}>
              <Zap className="w-4 h-4" style={{ color: "var(--sdb-accent)" }} strokeWidth={1.5} />
              Unlock unlimited generations
            </h3>
            <ul className="space-y-1.5 mb-4">
              {["Unlimited generations", "30-day data storage", "Priority queue"].map((item) => (
                <li key={item} className="flex items-center gap-2 font-mono text-xs" style={{ color: "var(--sdb-text-muted)" }}>
                  <Check className="w-3 h-3" style={{ color: "var(--sdb-accent)" }} strokeWidth={2} />
                  {item}
                </li>
              ))}
            </ul>
            <div className="font-mono text-[10px] tracking-[2px] uppercase mb-1" style={{ color: "var(--sdb-text-subtle)" }}>
              Purchase Credits
            </div>
            <PurchaseButtons />
          </div>
        </>
      )}

      {/* PAID TIER */}
      {user.is_paid && (
        <>
          <div className="font-mono text-[10px] tracking-[2px] uppercase" style={{ color: "var(--sdb-text-subtle)" }}>
            Credits
          </div>

          <div
            className="p-5 rounded-sm"
            style={{ background: "var(--sdb-surface)", border: "1px solid var(--sdb-border)" }}
          >
            <div className="text-4xl font-medium" style={{ color: "var(--sdb-accent)" }}>
              ${(user.credits_balance_cents / 100).toFixed(2)}
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--sdb-text-muted)" }}>
              Available balance
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] tracking-[2px] uppercase flex items-center gap-2 mb-1" style={{ color: "var(--sdb-text-subtle)" }}>
              <CreditCard className="w-3 h-3" strokeWidth={1.5} />
              Add Credits
            </div>
            <PurchaseButtons />
          </div>
        </>
      )}

      {/* Footer */}
      <div className="pt-4 border-t space-y-1" style={{ borderColor: "var(--sdb-border)" }}>
        <p className="font-mono text-[10px]" style={{ color: "var(--sdb-text-subtle)" }}>
          Credits never expire. Minimum purchase $5.00.
        </p>
        {!user.is_paid && (
          <p className="font-mono text-[10px]" style={{ color: "var(--sdb-text-subtle)" }}>
            Switch to paid to get 30-day data retention.
          </p>
        )}
      </div>
    </div>
  );
}
